import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  BarChart3,
  TrendingUp,
  Shield,
  DollarSign,
  Award,
  ChevronUp,
  ChevronDown,
  Minus,
  Lightbulb,
  Globe,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
interface BenchmarkMetrics {
  cost_per_mile: number;
  utilization_pct: number;
  maintenance_frequency: number;
  fuel_efficiency_mpg: number;
  safety_score: number;
  fleet_age_years: number;
  compliance_pct: number;
}

interface BenchmarkDoc {
  id: string;
  tenant_id: string;
  type: 'benchmark';
  period: string;
  metrics: BenchmarkMetrics;
  percentiles?: Record<string, number>;
  fleet_size: number;
  opt_in: boolean;
  recommendations?: string[];
  created_at: string;
  updated_at: string;
}

interface BenchmarkStatus {
  opt_in: boolean;
  latest: BenchmarkDoc | null;
  has_data: boolean;
}

interface Recommendation {
  metric: string;
  your_value: number;
  benchmark_median: number;
  percentile: number;
  recommendation: string;
  priority: 'high' | 'medium' | 'low';
}

interface RecommendationsData {
  recommendations: Recommendation[];
  period?: string;
  generated_at?: string;
  message?: string;
}

const MOCK_STATUS: BenchmarkStatus = {
  opt_in: true,
  has_data: true,
  latest: {
    id: 'bm1',
    tenant_id: 't1',
    type: 'benchmark',
    period: '2026-03',
    metrics: {
      cost_per_mile: 0.71,
      utilization_pct: 85,
      maintenance_frequency: 2.1,
      fuel_efficiency_mpg: 20.4,
      safety_score: 79,
      fleet_age_years: 4.2,
      compliance_pct: 97,
    },
    percentiles: {
      cost_per_mile: 42,
      utilization_pct: 72,
      maintenance_frequency: 55,
      fuel_efficiency_mpg: 38,
      safety_score: 44,
      fleet_age_years: 62,
      compliance_pct: 88,
    },
    fleet_size: 48,
    opt_in: true,
    recommendations: ['Cost per mile is above average.', 'Safety score needs improvement.'],
    created_at: '2026-03-10T03:00:00Z',
    updated_at: '2026-03-10T03:00:00Z',
  },
};

const MOCK_RECOMMENDATIONS: RecommendationsData = {
  recommendations: [
    {
      metric: 'Safety Score',
      your_value: 79,
      benchmark_median: 82,
      percentile: 44,
      recommendation: 'Safety score is below the fleet average. Implement driver training programs, review speeding and harsh-braking events, and enforce idling policies.',
      priority: 'high',
    },
    {
      metric: 'Cost Per Mile',
      your_value: 0.71,
      benchmark_median: 0.65,
      percentile: 42,
      recommendation: 'Your cost per mile is above the fleet average. Review fuel consumption, maintenance scheduling, and route optimization to reduce operating costs.',
      priority: 'medium',
    },
    {
      metric: 'Fuel Efficiency',
      your_value: 20.4,
      benchmark_median: 22,
      percentile: 38,
      recommendation: 'Fuel efficiency is below average. Schedule vehicle tune-ups, monitor tire pressure, and review idle time to improve MPG.',
      priority: 'medium',
    },
  ],
  period: '2026-03',
  generated_at: '2026-03-10T03:00:00Z',
};

const METRIC_CONFIG: {
  key: keyof BenchmarkMetrics;
  label: string;
  format: (v: number) => string;
  higherIsBetter: boolean;
  median: number;
  unit: string;
}[] = [
  { key: 'cost_per_mile', label: 'Cost Per Mile', format: v => `$${v.toFixed(2)}`, higherIsBetter: false, median: 0.65, unit: '/mi' },
  { key: 'utilization_pct', label: 'Fleet Utilization', format: v => `${v}%`, higherIsBetter: true, median: 78, unit: '%' },
  { key: 'maintenance_frequency', label: 'Maintenance Frequency', format: v => `${v}x/yr`, higherIsBetter: false, median: 2.4, unit: '/yr' },
  { key: 'fuel_efficiency_mpg', label: 'Fuel Efficiency', format: v => `${v} MPG`, higherIsBetter: true, median: 22, unit: 'MPG' },
  { key: 'safety_score', label: 'Safety Score', format: v => v.toFixed(0), higherIsBetter: true, median: 82, unit: '/100' },
  { key: 'fleet_age_years', label: 'Avg Fleet Age', format: v => `${v} yr`, higherIsBetter: false, median: 5, unit: 'yrs' },
  { key: 'compliance_pct', label: 'Compliance Rate', format: v => `${v}%`, higherIsBetter: true, median: 95, unit: '%' },
];

const PRIORITY_STYLES = {
  high: { bg: 'rgba(239,68,68,0.1)', text: '#EF4444', border: 'rgba(239,68,68,0.3)', label: 'High Priority' },
  medium: { bg: 'rgba(245,158,11,0.1)', text: '#F59E0B', border: 'rgba(245,158,11,0.3)', label: 'Medium Priority' },
  low: { bg: 'rgba(99,102,241,0.1)', text: '#818CF8', border: 'rgba(99,102,241,0.3)', label: 'Low Priority' },
};

function PercentileBar({ percentile }: { percentile: number; higherIsBetter: boolean }) {
  const color = percentile >= 70 ? '#10B981' : percentile >= 40 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${percentile}%`, background: color }}
        />
      </div>
      <span className="text-xs font-medium w-8 text-right" style={{ color }}>
        {percentile}%ile
      </span>
    </div>
  );
}

function TrendIcon({ percentile }: { percentile: number; higherIsBetter: boolean }) {
  if (percentile >= 60) return <ChevronUp className="w-4 h-4 text-emerald-400" />;
  if (percentile <= 40) return <ChevronDown className="w-4 h-4 text-red-400" />;
  return <Minus className="w-4 h-4 text-muted-foreground" />;
}

export default function BenchmarkPage() {
  const { data: statusData, isLoading: statusLoading, refetch: refetchStatus } = useApi<BenchmarkStatus>('/api/benchmarks');
  const { data: recsData, isLoading: recsLoading } = useApi<RecommendationsData>('/api/benchmarks/recommendations');

  const status = statusData ?? MOCK_STATUS;
  const recs = recsData ?? MOCK_RECOMMENDATIONS;
  const latest = status.latest;
  const percentiles = latest?.percentiles ?? {};
  const metrics = latest?.metrics;

  const [isTogglingOptIn, setIsTogglingOptIn] = useState(false);

  const handleOptInToggle = async () => {
    setIsTogglingOptIn(true);
    try {
      const resp = await fetch('/api/benchmarks/opt-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ opt_in: !status.opt_in }),
      });
      if (resp.ok) {
        refetchStatus?.();
      }
    } catch { /* ignore */ }
    setIsTogglingOptIn(false);
  };

  const percentileValues = Object.values(percentiles) as number[];
  const overallPercentile = percentileValues.length > 0
    ? Math.round(percentileValues.reduce((s: number, v: number) => s + v, 0) / percentileValues.length)
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fleet Benchmarking"
        description="Compare your fleet performance against the Collective benchmark pool"
        actions={(statusLoading || recsLoading) ? <Spinner size={16} /> : undefined}
      />

      {/* Opt-In Toggle */}
      <div
        className="flex items-center justify-between p-5 rounded-xl"
        style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          backdropFilter: 'blur(var(--glass-blur))',
        }}
      >
        <div className="flex items-center gap-3">
          <Globe className="w-5 h-5" style={{ color: 'var(--accent)' }} />
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
              Collective Benchmarking
              {status.opt_in && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
                  Active
                </span>
              )}
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              Share anonymized fleet metrics with the MSP Collective to receive industry percentile rankings. No PII or client data is shared.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleOptInToggle}
          disabled={isTogglingOptIn}
          className="flex items-center gap-2 text-sm font-medium transition-all px-4 py-2 rounded-lg"
          style={{
            background: status.opt_in ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)',
            color: status.opt_in ? '#10B981' : 'var(--muted-foreground)',
            border: `1px solid ${status.opt_in ? 'rgba(16,185,129,0.3)' : 'var(--glass-border)'}`,
          }}
        >
          {isTogglingOptIn ? <Spinner size={14} /> : status.opt_in
            ? <ToggleRight className="w-5 h-5" />
            : <ToggleLeft className="w-5 h-5" />}
          {status.opt_in ? 'Opted In' : 'Opt In'}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={DollarSign}
          label="Cost/Mile Percentile"
          value={percentiles['cost_per_mile'] != null ? `${percentiles['cost_per_mile']}%ile` : '—'}
        />
        <StatCard
          icon={BarChart3}
          label="Utilization Percentile"
          value={percentiles['utilization_pct'] != null ? `${percentiles['utilization_pct']}%ile` : '—'}
          accent="success"
        />
        <StatCard
          icon={Shield}
          label="Safety Percentile"
          value={percentiles['safety_score'] != null ? `${percentiles['safety_score']}%ile` : '—'}
          accent={
            (percentiles['safety_score'] ?? 50) < 40 ? 'destructive'
            : (percentiles['safety_score'] ?? 50) > 60 ? 'success'
            : undefined
          }
        />
        <StatCard
          icon={Award}
          label="Overall Rank"
          value={overallPercentile != null ? `Top ${100 - overallPercentile}%` : '—'}
        />
      </div>

      {/* Metrics Table */}
      {metrics && (
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-5 py-4" style={{ borderBottom: '1px solid var(--glass-border)' }}>
              <TrendingUp className="w-4 h-4" style={{ color: 'var(--accent)' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                Metrics vs Fleet Average
              </h3>
              {latest?.period && (
                <span className="ml-auto text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  Period: {latest.period}
                </span>
              )}
            </div>

            <div className="divide-y">
              {METRIC_CONFIG.map(cfg => {
                const value = metrics[cfg.key];
                const pct = percentiles[cfg.key as string] ?? 50;
                return (
                  <div
                    key={cfg.key as string}
                    className="grid grid-cols-12 items-center gap-4 px-5 py-4"
                    style={{ borderBottom: '1px solid var(--glass-border)' }}
                  >
                    <div className="col-span-3">
                      <div className="flex items-center gap-2">
                        <TrendIcon percentile={pct} higherIsBetter={cfg.higherIsBetter} />
                        <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{cfg.label}</span>
                      </div>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{cfg.format(value)}</span>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Your fleet</div>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{cfg.format(cfg.median)}</span>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Fleet avg</div>
                    </div>
                    <div className="col-span-5">
                      <PercentileBar percentile={pct} higherIsBetter={cfg.higherIsBetter} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Data State */}
      {!status.has_data && (
        <div
          className="text-center py-12 rounded-xl"
          style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            backdropFilter: 'blur(var(--glass-blur))',
          }}
        >
          <BarChart3 className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--muted-foreground)' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>No benchmark data yet</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
            Enable opt-in above. Benchmark data is generated weekly on Monday at 3 AM.
          </p>
        </div>
      )}

      {/* Recommendations */}
      {recs.recommendations.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
              AI Recommendations ({recs.recommendations.length})
            </h3>
          </div>
          {recs.recommendations.map((rec, idx) => {
            const style = PRIORITY_STYLES[rec.priority] ?? PRIORITY_STYLES.low;
            return (
              <div
                key={idx}
                className="p-4 rounded-xl"
                style={{ background: style.bg, border: `1px solid ${style.border}` }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{rec.metric}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: `${style.text}20`, color: style.text }}>
                        {style.label}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        {rec.percentile}th percentile
                      </span>
                    </div>
                    <p className="text-xs mt-2 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                      {rec.recommendation}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        Your value: <strong style={{ color: 'var(--foreground)' }}>{rec.your_value}</strong>
                      </span>
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        Fleet avg: <strong style={{ color: 'var(--foreground)' }}>{rec.benchmark_median}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {recs.message && recs.recommendations.length === 0 && (
        <div
          className="flex items-center gap-3 p-4 rounded-xl"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(var(--glass-blur))' }}
        >
          <Lightbulb className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{recs.message}</p>
        </div>
      )}
    </div>
  );
}
